/**
 * Image URL sizing helper.
 *
 * Centralizes the "ask the right CDN for the right size" decision so every
 * <img> in the app can request a sensibly-sized variant instead of pulling
 * the full-resolution original. Knows about:
 *
 *   - Supabase Storage public URLs (this project's bucket)
 *       /object/public/<bucket>/<key>
 *         → /render/image/public/<bucket>/<key>?width=N&resize=contain&quality=Q
 *       Already-transformed URLs (existing ?width=…) get their query
 *       params overridden so callers always win.
 *       `resize=contain` is REQUIRED, not cosmetic: Supabase's render
 *       endpoint defaults to resize=cover, and with a width but no height
 *       that squeezes the image to N px wide while KEEPING the original
 *       height — a 3024×4032 phone photo comes back 800×4032. Downstream
 *       `object-cover` then shows a stretched sliver of the frame (looked
 *       like a broken zoom on listing cards). `contain` + no height gives
 *       exactly N px wide and a proportional height, with no padding.
 *
 *   - Storyblok asset URLs (a.storyblok.com)
 *       /f/<space>/<dims>/<hash>/<file>
 *         → /f/<space>/<dims>/<hash>/<file>/m/<width>x0/filters:format(webp):quality(Q)
 *       Already-resized URLs (existing /m/<w>x<h>/filters…) get the size
 *       segment stripped and replaced.
 *
 *   - Anything else: returned unchanged.
 *
 * Browsers send Accept: image/webp by default; Supabase's render endpoint
 * negotiates format from that header, so we deliberately do NOT pass
 * `&format=webp` for Supabase URLs. Storyblok's image service requires
 * explicit `filters:format(webp)` so we always include it there.
 *
 * We size by width only and let each service derive the height; CSS handles
 * the box. For Supabase that means passing `resize=contain` (see above);
 * for Storyblok it means the `x0` height segment.
 *
 * Pure, no side effects, safe to call from any render path.
 */

const SUPABASE_OBJECT_MARKER = "/storage/v1/object/public/";
const SUPABASE_RENDER_MARKER = "/storage/v1/render/image/public/";

const DEFAULT_QUALITY = 75;

export type ImageOptions = {
  /** Target rendered width in CSS pixels. Use the largest size you'd ever
   *  render at (the CDN may serve more for retina via srcset). */
  width: number;
  /** 20..100. Defaults to 75 — visually indistinguishable from 90 for
   *  photos, ~2x smaller bytes. */
  quality?: number;
};

/**
 * Return a sized variant of `url`, or pass through unchanged if we don't
 * know how to size it. Returns `null` for null/empty/non-string input.
 */
export function getImageUrl(
  url: string | null | undefined,
  opts: ImageOptions
): string | null {
  if (typeof url !== "string" || !url.trim()) return null;
  const width = Math.round(opts.width);
  if (!Number.isFinite(width) || width < 1) return url;
  const quality = clampQuality(opts.quality ?? DEFAULT_QUALITY);

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  if (isSupabaseStorageHost(parsed.hostname)) {
    return sizeSupabase(parsed, width, quality);
  }
  if (isStoryblokHost(parsed.hostname)) {
    return sizeStoryblok(parsed, width, quality);
  }
  return url;
}

/**
 * Generate a `srcset` string for a responsive <img>. Caller provides the
 * candidate widths; the browser picks based on viewport + DPR.
 *
 *   <img
 *     src={getImageUrl(url, {width: 400})}
 *     srcSet={getImageSrcSet(url, [400, 800, 1200])}
 *     sizes="(max-width: 640px) 100vw, 400px"
 *   />
 */
export function getImageSrcSet(
  url: string | null | undefined,
  widths: number[],
  opts?: Omit<ImageOptions, "width">
): string | undefined {
  if (typeof url !== "string" || !url.trim()) return undefined;
  if (!Array.isArray(widths) || widths.length === 0) return undefined;
  const parts: string[] = [];
  for (const w of widths) {
    const sized = getImageUrl(url, { width: w, quality: opts?.quality });
    if (sized) parts.push(`${sized} ${Math.round(w)}w`);
  }
  return parts.length ? parts.join(", ") : undefined;
}

// ─── Supabase ────────────────────────────────────────────────────────────────

function isSupabaseStorageHost(hostname: string): boolean {
  return hostname.endsWith(".supabase.co") || hostname.endsWith(".supabase.in");
}

function sizeSupabase(u: URL, width: number, quality: number): string {
  let pathname = u.pathname;
  if (pathname.includes(SUPABASE_OBJECT_MARKER)) {
    pathname = pathname.replace(SUPABASE_OBJECT_MARKER, SUPABASE_RENDER_MARKER);
  } else if (!pathname.includes(SUPABASE_RENDER_MARKER)) {
    // Some other Supabase URL shape (signed URLs, function endpoints, etc.) —
    // don't try to be clever.
    return u.toString();
  }

  const params = new URLSearchParams(u.search);
  params.set("width", String(width));
  params.set("quality", String(quality));
  // `contain` (not the endpoint's default `cover`) is what makes a
  // width-only request aspect-preserving — see the module header. Any
  // pre-existing `height` is dropped so the caller's width stays the only
  // constraint (otherwise `contain` would fit inside that stale box).
  params.set("resize", "contain");
  params.delete("height");
  // Intentionally leave `format` unset so Accept-header negotiation wins.

  const next = new URL(u.toString());
  next.pathname = pathname;
  next.search = params.toString();
  return next.toString();
}

// ─── Storyblok ───────────────────────────────────────────────────────────────

function isStoryblokHost(hostname: string): boolean {
  return hostname === "a.storyblok.com" || hostname.endsWith(".storyblok.com");
}

/**
 * Storyblok asset paths look like:
 *   /f/<space>/<dims>/<hash>/<file.ext>
 * Optionally followed by an existing transform:
 *   /m/<W>x<H>/filters:<...>
 *
 * We strip any existing transform suffix and append a canonical one.
 */
function sizeStoryblok(u: URL, width: number, quality: number): string {
  const pathname = stripStoryblokTransform(u.pathname);
  const transform = `/m/${width}x0/filters:format(webp):quality(${quality})`;
  const next = new URL(u.toString());
  next.pathname = pathname + transform;
  // Drop any pre-existing query (Storyblok asset URLs don't use ?params).
  next.search = "";
  return next.toString();
}

/** Remove an existing `/m/...` (and optional `/filters:...`) segment from a Storyblok path. */
function stripStoryblokTransform(pathname: string): string {
  // Matches `/m/<size>` optionally followed by `/filters:...` at end of path.
  return pathname.replace(/\/m\/[^/]+(\/filters:[^/]*)?$/, "");
}

// ─── Util ────────────────────────────────────────────────────────────────────

function clampQuality(q: number): number {
  if (!Number.isFinite(q)) return DEFAULT_QUALITY;
  if (q < 20) return 20;
  if (q > 100) return 100;
  return Math.round(q);
}
